/**
 * Whether an address is one a new browser tab can be trusted to open.
 *
 * Only `http` and `https`. Everything else — `javascript:`, `mailto:`, a relative
 * path, an empty href — is shown as text wherever this is consulted (spec 096
 * FR-002). Rendered prose asks `linkTarget` instead, which also lets `mailto:`
 * through; this stays the rule for a link that opens a tab. Kept beside `ExternalLink` rather than inside it so the component file
 * exports only a component (fast refresh) and the rule can be tested on its own.
 */
export function isWebAddress(href: string | undefined | null): href is string {
  return typeof href === 'string' && /^https?:\/\//i.test(href);
}

/** The host of a web address (callers have checked `isWebAddress`, so it parses). */
export function hostOf(href: string): string {
  return new URL(href).host;
}

/**
 * Where a link in rendered prose may go (spec 145 FR-007):
 *
 * - `'app'`: a web address on this app's own origin, opened in the same tab,
 *   inside the app. Compared against the origin the app is served from, so a
 *   link pasted from the local stack stays in the local stack.
 * - `'web'`: any other web address, opened in a new tab.
 * - `'mail'`: a `mailto:` address, handed to the mail app.
 * - `'none'`: anything else — `javascript:`, a relative path, an empty href —
 *   shown as its text.
 *
 * A relative path is `'none'` even though it is on this origin: it was refused
 * before spec 145, and text written then renders as it did.
 */
export type LinkTarget = 'app' | 'web' | 'mail' | 'none';

export function linkTarget(
  href: string | undefined | null,
  origin: string = window.location.origin,
): LinkTarget {
  if (typeof href !== 'string') return 'none';
  if (/^mailto:[^\s]+$/i.test(href)) return 'mail';
  if (!isWebAddress(href)) return 'none';
  try {
    return new URL(href).origin === origin ? 'app' : 'web';
  } catch {
    // Looks like a web address and does not parse: not one to open.
    return 'none';
  }
}
